import { createButton } from '@gluestack-ui/core/button/creator'
import { UIIcon } from '@gluestack-ui/core/icon/creator'
import { withStyleContext } from '@gluestack-ui/utils/nativewind-utils'
import { buttonContract, type ContractVariant } from 'aos-ds/src/primitives/button/button.contract'
import { Typography } from 'aos-ds/src/typography/Typography'
import { variantForStyleId } from 'aos-ds/src/typography/variants'
import { cssInterop } from 'nativewind'
import React, { cloneElement, forwardRef, isValidElement, type ReactNode } from 'react'
import { ActivityIndicator, Pressable, Text, View } from 'react-native'

import { FOCUS_RING_CLASS } from '../../focusRing'
import { twMerge } from '../../tailwind/merge'

const Root = withStyleContext(Pressable, 'BUTTON')

const UIButton = createButton({ Root, Text, Group: View, Spinner: ActivityIndicator, Icon: UIIcon })

cssInterop(UIButton, { className: 'style' })

export const BUTTON_SIZES = buttonContract.props.size.values
export const BUTTON_TYPES = buttonContract.props.type.values

export type ButtonSize = (typeof BUTTON_SIZES)[number]
export type ButtonType = (typeof BUTTON_TYPES)[number]

export const LABEL_VARIANT = variantForStyleId(buttonContract.textStyleIds[0])

export const LABEL_JUSTIFY = buttonContract.disagreeing.some(
    entry => entry.property === 'primaryAxisAlignItems' && entry.stated === 'CENTER',
)
    ? 'justify-center'
    : undefined

export const buttonVariantKey = (size: ButtonSize, type: ButtonType): string =>
    `size=${size}, type=${type}`

export const resolveButton = (size: ButtonSize, type: ButtonType) => {
    const key = buttonVariantKey(size, type)
    const variant = (buttonContract.variants as Record<string, ContractVariant>)[key]
    if (!variant) {
        throw new Error(`Button: the Figma contract has no "${key}"`)
    }
    return variant
}

export type ButtonProps = Omit<
    React.ComponentProps<typeof UIButton>,
    'style' | 'children' | 'size'
> & {
    size?: ButtonSize
    type?: ButtonType
    iconLeft?: ReactNode
    iconRight?: ReactNode
    className?: string
    children?: ReactNode
}

type ButtonRef = React.ComponentRef<typeof UIButton>

const withoutInteraction = (classes: string): string =>
    classes
        .split(' ')
        .filter(
            utility =>
                !utility.startsWith('hover:') &&
                !utility.startsWith('active:') &&
                !utility.startsWith('group-hover/button:') &&
                !utility.startsWith('group-active/button:'),
        )
        .join(' ')

const inkOf = (label: string): string =>
    label
        .split(' ')
        .filter(utility => utility !== 'text-left')
        .join(' ')

const inked = (icon: ReactNode, label: string): ReactNode =>
    isValidElement<{ className?: string }>(icon)
        ? cloneElement(icon, { className: twMerge(inkOf(label), icon.props.className) })
        : icon

/**
 * The Figma Button. Every class comes from the contract cell for its `size` and
 * `type`; the additions below are the only ones, and each has a stated source.
 *
 * - `flex-row`: Figma's layout is HORIZONTAL. The contract leaves it out because a
 *   row is the web default, but React Native defaults to a column.
 * - `justify-center`: read from the contract's `disagreeing`. The file states
 *   primaryAxisAlignItems CENTER on every cell, but each cell hugs its content, so
 *   there is no slack to centre in and the generator emits no class. Design
 *   confirmed on 20.10.26 that the label is centred; it only shows once a call site
 *   stretches the button.
 * - the icon ink: Design confirmed on 20.10.26 that the icon is always the colour of
 *   the text. It is not in the Figma file, so `contract-fidelity.spec.tsx` names it
 *   as an exception to delete once the file says it.
 * - the focus ring: the set has no focus state. Its colour is a token of this
 *   package's own Tailwind preset.
 *
 * While disabled, the hover and pressed utilities go too: Figma's disabled cell keeps
 * the default fill and changes only the opacity.
 */
export const Button = forwardRef<ButtonRef, ButtonProps>(
    (
        {
            size = 'small',
            type = 'primary',
            iconLeft,
            iconRight,
            disabled,
            isDisabled,
            className,
            children,
            ...rest
        },
        ref,
    ) => {
        const variant = resolveButton(size, type)
        const isOff = disabled ?? isDisabled ?? false
        const label = isOff ? withoutInteraction(variant.label) : variant.label
        return (
            <UIButton
                ref={ref}
                disabled={isOff}
                isDisabled={isOff}
                className={twMerge(
                    FOCUS_RING_CLASS,
                    'flex-row',
                    LABEL_JUSTIFY,
                    isOff ? withoutInteraction(variant.container) : variant.container,
                    isOff ? variant.disabled : undefined,
                    className,
                )}
                {...rest}
            >
                {inked(iconLeft, label)}
                {children === undefined ? null : (
                    <View className={variant.parts['text-wrapper']}>
                        <Typography variant={LABEL_VARIANT} className={label}>
                            {children}
                        </Typography>
                    </View>
                )}
                {inked(iconRight, label)}
            </UIButton>
        )
    },
)

Button.displayName = 'Button'
